import { Room, RoomEvent } from 'livekit-client';

class LiveKitService {
  constructor() {
    this.room = null;
    this.isConnected = false;
    this.onAudioData = null;
    this.onConnectionStateChange = null;
    this.onParticipantConnected = null;
    this.onParticipantDisconnected = null;
    this.onTrackSubscribed = null;
    this.onTrackUnsubscribed = null;
    this.onDataReceived = null;
  }

  async connect(livekitUrl, token, roomName) {
    try {
      console.log('🔗 LiveKit: Starting connection...', { livekitUrl, roomName });
      
      this.room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      // Set up event listeners with detailed logging
      this.setupEventListeners();

      console.log('🔗 LiveKit: Connecting to room...');
      await this.room.connect(livekitUrl, token, {
        autoSubscribe: true,
      });

      console.log('✅ LiveKit: Successfully connected to room');
      console.log('🔊 LiveKit: Local participant:', this.room.localParticipant.identity);
      console.log('👥 LiveKit: Remote participants:', this.room.participants ? this.room.participants.size : 0);

      this.isConnected = true;
      return true;
    } catch (error) {
      console.error('❌ LiveKit: Connection failed:', error);
      throw error;
    }
  }

  setupEventListeners() {
    if (!this.room) return;

    // Connection state changes
    this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
      console.log('🔄 LiveKit: Connection state changed:', state);
      if (this.onConnectionStateChange) {
        this.onConnectionStateChange(state);
      }
    });

    // Participant connected
    this.room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log('👤 LiveKit: Participant connected:', participant.identity);
      if (this.onParticipantConnected) {
        this.onParticipantConnected(participant);
      }
    });

    // Participant disconnected
    this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      console.log('👤 LiveKit: Participant disconnected:', participant.identity);
      if (this.onParticipantDisconnected) {
        this.onParticipantDisconnected(participant);
      }
    });

    // Track subscribed
    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log('🎵 LiveKit: Track subscribed:', {
        track: track.kind,
        participant: participant.identity,
        sid: publication.trackSid
      });
      if (this.onTrackSubscribed) {
        this.onTrackSubscribed(track, publication, participant);
      }
    });

    // Track unsubscribed
    this.room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      console.log('🎵 LiveKit: Track unsubscribed:', {
        track: track.kind,
        participant: participant.identity,
        sid: publication.trackSid
      });
      if (this.onTrackUnsubscribed) {
        this.onTrackUnsubscribed(track, publication, participant);
      }
    });

    // Data received
// In livekitService.js - Update the data received handler in setupEventListeners
this.room.on(RoomEvent.DataReceived, (payload, participant) => {
  try {
      const decoder = new TextDecoder('utf-8');
      const messageStr = decoder.decode(payload);
      
      try {
          // Try to parse as JSON
          const message = JSON.parse(messageStr);
          console.log('📨 LiveKit: Received data message:', {
              from: participant.identity,
              data: message
          });
          
          if (this.onDataReceived) {
              this.onDataReceived(message, participant);
          }
      } catch (e) {
          // If not valid JSON, pass as raw text
          console.log('📨 LiveKit: Received text message:', {
              from: participant.identity,
              text: messageStr
          });
          
          if (this.onDataReceived) {
              this.onDataReceived({
                  type: 'text',
                  text: messageStr
              }, participant);
          }
      }
  } catch (error) {
      console.error('❌ LiveKit: Error processing data message:', error);
  }
});

    // Audio levels
    this.room.on(RoomEvent.AudioPlaybackStatusChanged, (playing) => {
      console.log('🔊 LiveKit: Audio playback status:', playing);
    });

    // Local participant events
    this.room.localParticipant.on('trackPublished', (publication) => {
      console.log('🎤 LiveKit: Local track published:', publication.track.kind);
    });

    this.room.localParticipant.on('trackUnpublished', (publication) => {
      console.log('🎤 LiveKit: Local track unpublished:', publication.track.kind);
    });
  }

  async enableMicrophone() {
    try {
      if (!this.room) {
        console.error('❌ LiveKit: Cannot enable microphone - not connected');
        return false;
      }

      console.log('🎤 LiveKit: Enabling microphone...');
      await this.room.localParticipant.setMicrophoneEnabled(true);
      console.log('✅ LiveKit: Microphone enabled');
      return true;
    } catch (error) {
      console.error('❌ LiveKit: Failed to enable microphone:', error);
      return false;
    }
  }

  async disableMicrophone() {
    try {
      if (!this.room) {
        console.error('❌ LiveKit: Cannot disable microphone - not connected');
        return false;
      }

      console.log('🎤 LiveKit: Disabling microphone...');
      await this.room.localParticipant.setMicrophoneEnabled(false);
      console.log('✅ LiveKit: Microphone disabled');
      return true;
    } catch (error) {
      console.error('❌ LiveKit: Failed to disable microphone:', error);
      return false;
    }
  }

  async enableSpeaker() {
    try {
      if (!this.room) {
        console.error('❌ LiveKit: Cannot enable speaker - not connected');
        return false;
      }

      console.log('🔊 LiveKit: Enabling speaker...');
      await this.room.localParticipant.setCameraEnabled(false);
      console.log('✅ LiveKit: Speaker enabled');
      return true;
    } catch (error) {
      console.error('❌ LiveKit: Failed to enable speaker:', error);
      return false;
    }
  }

  getConnectionState() {
    if (!this.room) return 'disconnected';
    return this.room.connectionState;
  }

  getParticipants() {
    if (!this.room) return [];
    
    // Get all participants including local and remote
    const participants = [];
    
    // Add local participant
    if (this.room.localParticipant) {
      participants.push(this.room.localParticipant);
    }
    
    // Add remote participants
    if (this.room.participants) {
      participants.push(...Array.from(this.room.participants.values()));
    }
    
    return participants;
  }

  getLocalParticipant() {
    if (!this.room) return null;
    return this.room.localParticipant;
  }

  async disconnect() {
    try {
      if (!this.room) {
        console.log('🔗 LiveKit: Already disconnected');
        return;
      }

      console.log('🔗 LiveKit: Disconnecting...');
      await this.room.disconnect();
      this.room = null;
      this.isConnected = false;
      console.log('✅ LiveKit: Disconnected successfully');
    } catch (error) {
      console.error('❌ LiveKit: Error during disconnect:', error);
    }
  }

  // Set callback functions
  setOnAudioData(callback) {
    this.onAudioData = callback;
  }

  setOnConnectionStateChange(callback) {
    this.onConnectionStateChange = callback;
  }

  setOnParticipantConnected(callback) {
    this.onParticipantConnected = callback;
  }

  setOnParticipantDisconnected(callback) {
    this.onParticipantDisconnected = callback;
  }

  setOnTrackSubscribed(callback) {
    this.onTrackSubscribed = callback;
  }

  setOnTrackUnsubscribed(callback) {
    this.onTrackUnsubscribed = callback;
  }

  setOnDataReceived(callback) {
    this.onDataReceived = callback;
  }

  // Send data via LiveKit data channel
// In livekitService.js - Update the sendData method
async sendData(data) {
  try {
      if (!this.room || !this.isConnected) {
          console.error('❌ LiveKit: Cannot send data - not connected');
          return false;
      }

      // Ensure data is a proper object with type and message
      const message = typeof data === 'string' 
          ? { type: 'chat', message: data } 
          : data;

      // Stringify the message
      const messageStr = JSON.stringify(message);
      const encoder = new TextEncoder();
      const payload = encoder.encode(messageStr);
      
      console.log('📤 LiveKit: Sending data:', message);
      await this.room.localParticipant.publishData(payload);
      console.log('✅ LiveKit: Data sent successfully');
      return true;
  } catch (error) {
      console.error('❌ LiveKit: Failed to send data:', error);
      return false;
  }
}
  // Get room instance for advanced operations
  getRoom() {
    return this.room;
  }
}

const livekitService = new LiveKitService();
export default livekitService;